import React, { ReactNode } from "react";
import { ResponsiveContainer, BarChart, Bar, Cell, Tooltip, CartesianGrid, XAxis, YAxis, ReferenceLine, LabelList } from "recharts";

import { WidgetTheme } from "../../types/analytics";
import { truncateAxisLabelToWidth } from "../../screens/analytics/widgets/utils";
import WidgetCard from "./WidgetCard";

export type RankedBarDatum = {
    label: string;
    value: number;
    fill?: string;
    actualChange?: number;
};

type RankedBarScaleMode = "linear" | "sqrt";
type RankedBarPresentation = "standard" | "diverging";
type RankedBarDivergingDomain = "balanced" | "positive";

type RankedBarChartDatum = RankedBarDatum & {
    chartValue: number;
    rawValue: number;
};

type RankedBarWidgetProps = {
    title: string;
    description?: string;
    theme: WidgetTheme;
    data: RankedBarDatum[];
    isLoading?: boolean;
    error?: string | null;
    emptyMessage?: string;
    minHeight?: number;
    rowHeight?: number;
    axisWidth?: number;
    axisTextMaxWidth?: number;
    sortComparator?: (a: RankedBarDatum, b: RankedBarDatum) => number;
    labelFormatter?: (label: string) => string;
    xAxisTickFormatter?: (value: number) => string;
    tooltipLabelFormatter?: (label: ReactNode) => ReactNode;
    tooltipValueFormatter?: (value: number, item: RankedBarDatum) => [string, string] | string;
    xAxisScaleMode?: RankedBarScaleMode;
    presentation?: RankedBarPresentation;
    divergingDomain?: RankedBarDivergingDomain;
    divergingMargin?: { left?: number; right?: number };
    divergingLabelMaxWidth?: number;
    valueLabelFormatter?: (value: number, item: RankedBarDatum) => string;
};

const DEFAULT_AXIS_WIDTH = 280;
const DEFAULT_AXIS_TEXT_MAX_WIDTH = 236;
const DIVERGING_EDGE_MARGIN = 85;
const DEFAULT_DIVERGING_LABEL_MAX_WIDTH = 190;

const RankedBarWidget: React.FC<RankedBarWidgetProps> = ({
    title,
    description,
    theme,
    data,
    isLoading = false,
    error = null,
    emptyMessage = "No data available for the selected date range.",
    minHeight = 280,
    rowHeight = 28,
    axisWidth = DEFAULT_AXIS_WIDTH,
    axisTextMaxWidth = DEFAULT_AXIS_TEXT_MAX_WIDTH,
    sortComparator,
    labelFormatter,
    xAxisTickFormatter,
    tooltipLabelFormatter,
    tooltipValueFormatter,
    xAxisScaleMode = "linear",
    presentation = "standard",
    divergingDomain = "balanced",
    divergingMargin,
    divergingLabelMaxWidth = DEFAULT_DIVERGING_LABEL_MAX_WIDTH,
    valueLabelFormatter
}) => {
    const transformValue = (value: number): number => {
        if (xAxisScaleMode === "sqrt") {
            return Math.sign(value) * Math.sqrt(Math.abs(value));
        }

        return value;
    };

    const inverseTransformValue = (value: number): number => {
        if (xAxisScaleMode === "sqrt") {
            return Math.sign(value) * Math.pow(Math.abs(value), 2);
        }

        return value;
    };

    const sortedData: RankedBarChartDatum[] = [...data]
        .filter(item => Number.isFinite(item.value))
        .sort(sortComparator || ((a, b) => b.value - a.value))
        .map(item => ({
            ...item,
            rawValue: item.value,
            chartValue: transformValue(item.value)
        }));

    const chartHeight = Math.max(minHeight, sortedData.length * rowHeight);

    const rawValues = sortedData.map(item => item.rawValue);
    const rawMin = rawValues.length > 0 ? Math.min(...rawValues) : 0;
    const rawMax = rawValues.length > 0 ? Math.max(...rawValues) : 0;

    const roundTickToNearestTen = (value: number): number => {
        if (value === 0) {
            return 0;
        }

        if (Math.abs(value) < 10) {
            return Math.round(value);
        }

        return Math.round(value / 10) * 10;
    };

    const buildRawTicks = (): number[] | undefined => {
        if (sortedData.length === 0 || xAxisScaleMode === "linear") {
            return undefined;
        }

        const tickSet = new Set<number>();
        const addTick = (tick: number): void => {
            if (Number.isFinite(tick)) {
                tickSet.add(roundTickToNearestTen(tick));
            }
        };

        addTick(rawMin);
        addTick(rawMax);
        addTick(0);

        const positiveMax = Math.max(0, rawMax);
        const negativeMin = Math.min(0, rawMin);

        if (positiveMax > 0) {
            [0.25, 0.5, 0.75].forEach(step => addTick(positiveMax * step));
        }

        if (negativeMin < 0) {
            [0.25, 0.5, 0.75].forEach(step => addTick(negativeMin * step));
        }

        return Array.from(tickSet)
            .sort((a, b) => a - b)
            .filter((tick, index, ticks) => index === 0 || Math.abs(tick - ticks[index - 1]) > 0.000001);
    };

    const axisTicks = buildRawTicks()?.map(transformValue);

    const getAxisFont = (): string => {
        if (typeof document === "undefined") {
            return "14px sans-serif";
        }

        const bodyStyle = window.getComputedStyle(document.body);
        return bodyStyle.font || `${bodyStyle.fontSize || "14px"} ${bodyStyle.fontFamily || "sans-serif"}`;
    };

    const renderYAxisTick = (tickProps: any): ReactNode => {
        const { x, y, payload } = tickProps;
        const fullLabel = String(payload?.value ?? "");
        const displayLabel = labelFormatter ? labelFormatter(fullLabel) : fullLabel;
        const axisFont = getAxisFont();
        const truncatedLabel = truncateAxisLabelToWidth(displayLabel, axisTextMaxWidth, axisFont);

        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dominantBaseline="middle" textAnchor="end" fill={theme.text} style={{ font: axisFont }}>
                    <title>{displayLabel}</title>
                    {truncatedLabel}
                </text>
            </g>
        );
    };

    const renderDivergingBarLabel = (labelProps: any): JSX.Element | null => {
        const { index, payload, viewBox } = labelProps;
        const { x, y, width, height } = viewBox || labelProps;
        const datum = (payload as RankedBarChartDatum | undefined)
            || (typeof index === "number" ? sortedData[index] : undefined);

        if (!datum || [x, y, width, height].some(value => typeof value !== "number")) {
            return null;
        }

        const rawValue = datum.rawValue;
        const isRiser = divergingDomain === "positive" || rawValue >= 0;
        const fullLabel = labelFormatter ? labelFormatter(datum.label) : datum.label;
        const axisFont = getAxisFont();
        const truncatedLabel = truncateAxisLabelToWidth(fullLabel, divergingLabelMaxWidth, axisFont);
        const valueLabel = valueLabelFormatter
            ? valueLabelFormatter(rawValue, datum)
            : Math.round(rawValue).toLocaleString();
        const textY = y + height / 2;
        const barLeft = Math.min(x, x + width);
        const barRight = Math.max(x, x + width);

        return (
            <g style={{ font: axisFont, fontSize: 11 }} fill={theme.text}>
                {isRiser ? (
                    <>
                        <text x={barLeft - 7} y={textY} dominantBaseline="middle" textAnchor="end">
                            <title>{fullLabel}</title>
                            {truncatedLabel}
                        </text>
                        <text x={barRight + 7} y={textY} dominantBaseline="middle" textAnchor="start">
                            {valueLabel}
                        </text>
                    </>
                ) : (
                    <>
                        <text x={barLeft - 7} y={textY} dominantBaseline="middle" textAnchor="end">
                            {valueLabel}
                        </text>
                        <text x={barRight + 7} y={textY} dominantBaseline="middle" textAnchor="start">
                            <title>{fullLabel}</title>
                            {truncatedLabel}
                        </text>
                    </>
                )}
            </g>
        );
    };

    return (
        <WidgetCard
            title={title}
            description={description}
            theme={theme}
            isLoading={isLoading}
            error={error}
            isEmpty={!isLoading && !error && sortedData.length === 0}
            emptyMessage={emptyMessage}
        >
            <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart
                    data={sortedData}
                    layout="vertical"
                    margin={presentation === "diverging"
                        ? {
                            top: 5,
                            right: divergingMargin?.right ?? DIVERGING_EDGE_MARGIN,
                            left: divergingMargin?.left ?? DIVERGING_EDGE_MARGIN,
                            bottom: 5
                        }
                        : { top: 5, right: 24, left: 12, bottom: 5 }}
                >
                    <CartesianGrid
                        stroke={theme.border}
                        strokeDasharray="3 3"
                        vertical={presentation !== "diverging"}
                        horizontal={presentation !== "diverging"}
                    />
                    <XAxis
                        type="number"
                        hide={presentation === "diverging"}
                        tick={{ fill: theme.text }}
                        axisLine={{ stroke: theme.border }}
                        tickLine={{ stroke: theme.border }}
                        domain={presentation === "diverging" && divergingDomain === "positive" ? [0, "dataMax"] : undefined}
                        ticks={axisTicks}
                        tickFormatter={(value: number) => {
                            const rawValue = inverseTransformValue(value);
                            return xAxisTickFormatter ? xAxisTickFormatter(rawValue) : Math.round(rawValue).toLocaleString();
                        }}
                    />
                    <YAxis
                        type="category"
                        dataKey="label"
                        width={presentation === "diverging" ? 0 : axisWidth}
                        interval={0}
                        tick={presentation === "diverging" ? false : renderYAxisTick}
                        axisLine={presentation === "diverging" ? false : { stroke: theme.border }}
                        tickLine={presentation === "diverging" ? false : { stroke: theme.border }}
                    />
                    {presentation === "diverging" && <ReferenceLine x={0} stroke={theme.border} strokeWidth={1} />}
                    <Tooltip
                        contentStyle={{
                            backgroundColor: theme.chartBg,
                            border: `1px solid ${theme.border}`,
                            color: theme.text
                        }}
                        labelStyle={{ color: theme.text }}
                        itemStyle={{ color: theme.text }}
                        labelFormatter={tooltipLabelFormatter}
                        formatter={(rawValue: number | string | Array<number | string> | undefined, _name?: string, props?: any) => {
                            const parsedValue = typeof rawValue === "number" ? rawValue 
                                : typeof rawValue === "string" ? Number(rawValue)
                                    : Array.isArray(rawValue) ? Number(rawValue[0]) : 0;
                            const fallbackNumericValue = inverseTransformValue(Number.isFinite(parsedValue) ? parsedValue : 0);
                            const datum = props?.payload as RankedBarChartDatum | undefined;
                            const numericValue = datum?.rawValue ?? fallbackNumericValue;

                            if (tooltipValueFormatter) {
                                return tooltipValueFormatter(numericValue, datum || { label: "", value: numericValue, rawValue: numericValue, chartValue: transformValue(numericValue) });
                            }

                            return [numericValue.toLocaleString(), title];
                        }}
                    />
                    <Bar dataKey="chartValue" radius={presentation === "diverging" ? 0 : [0, 4, 4, 0]}>
                        {sortedData.map((entry, index) => (
                            <Cell key={`${title}-bar-${entry.label}-${index}`} fill={entry.fill || "#2dd4bf"} />
                        ))}
                        {presentation === "diverging" && <LabelList content={renderDivergingBarLabel} />}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </WidgetCard>
    );
};

export default RankedBarWidget;